import { FormEvent, useState } from 'react';
import { useCurrentUser, useAdminAuditLogs, useAdminInviteUser, useAdminTenants, useAdminUsers } from '../lib/hooks';
import { Button, CopyChip, Field, PageHeader, useToast } from '../components/ui';

export function AdminPage() {
  const { data: user } = useCurrentUser();
  const { toast } = useToast();

  const tenants = useAdminTenants();
  const users = useAdminUsers();
  const auditLogs = useAdminAuditLogs();
  const inviteMutation = useAdminInviteUser();
  const [inviteToken, setInviteToken] = useState<string | null>(null);

  if (user?.globalRole !== 'SUPER_ADMIN') {
    return (
      <section className="card">
        <h2>Platform admin</h2>
        <p className="card-sub">This area is reserved for platform administrators.</p>
      </section>
    );
  }

  async function onInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);

    try {
      const response = (await inviteMutation.mutateAsync({
        tenantId: String(form.get('tenantId') || ''),
        email: String(form.get('email') || ''),
        role: String(form.get('role') || 'LOCATION_USER') as any,
        expiresInDays: Number(form.get('expiresInDays') || 7),
      })) as any;

      setInviteToken(response.token);
      formElement.reset();
      toast({ title: 'Invite created', description: 'Copy the one-time token below and send it to the user.' });
    } catch (error) {
      toast({ variant: 'error', title: 'Could not create invite', description: (error as Error).message });
    }
  }

  return (
    <>
      <PageHeader
        title="Platform admin"
        description="Cross-workspace view of every agency, user, and audit trail on the platform."
      />

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}>
        <section className="grid">
          <div className="card">
            <h3>Agency workspaces</h3>
            <div className="table-wrap" style={{ marginTop: 10 }}>
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Slug</th>
                    <th>Mode</th>
                    <th>Locations</th>
                    <th>Members</th>
                  </tr>
                </thead>
                <tbody>
                  {(tenants.data || []).map((tenant: any) => (
                    <tr key={tenant.id}>
                      <td style={{ fontWeight: 600 }}>{tenant.name}</td>
                      <td className="mono">{tenant.slug}</td>
                      <td>{tenant.mode}</td>
                      <td>{tenant._count?.locations || 0}</td>
                      <td>{tenant._count?.memberships || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <h3>Invite a user to any workspace</h3>
            <p className="card-sub">Generates a one-time invite token tied to the workspace and role you choose.</p>
            <form className="grid" onSubmit={onInvite}>
              <div className="form-grid">
                <Field label="Workspace ID" hint="Copy it from the workspaces table.">
                  <input name="tenantId" className="mono" placeholder="Workspace ID" required />
                </Field>
                <Field label="Email">
                  <input name="email" type="email" placeholder="person@example.com" required />
                </Field>
                <Field label="Role">
                  <select name="role" defaultValue="LOCATION_USER">
                    <option value="LOCATION_USER">Location member</option>
                    <option value="AGENCY">Agency owner</option>
                    <option value="AGENCY_DEMO">Agency (trial)</option>
                    <option value="ADMIN">Administrator</option>
                  </select>
                </Field>
                <Field label="Expires in (days)">
                  <input name="expiresInDays" type="number" defaultValue={7} min={1} max={30} />
                </Field>
              </div>
              <div>
                <Button type="submit" loading={inviteMutation.isPending} loadingText="Creating invite…">
                  Create invite
                </Button>
              </div>
            </form>

            {inviteToken && (
              <div style={{ marginTop: 12 }}>
                <span className="muted">One-time invite token — share it privately:</span>
                <div style={{ marginTop: 6 }}>
                  <CopyChip value={inviteToken} />
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="grid">
          <div className="card">
            <h3>All users</h3>
            <div className="table-wrap" style={{ marginTop: 10 }}>
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Global role</th>
                    <th>Workspaces</th>
                  </tr>
                </thead>
                <tbody>
                  {(users.data || []).slice(0, 100).map((entry: any) => (
                    <tr key={entry.id}>
                      <td style={{ fontWeight: 600 }}>{entry.name}</td>
                      <td>{entry.email}</td>
                      <td>{entry.globalRole}</td>
                      <td>{entry.memberships?.length || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <h3>Platform audit trail</h3>
            <div className="table-wrap" style={{ marginTop: 10 }}>
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Action</th>
                    <th>By</th>
                    <th>Target</th>
                  </tr>
                </thead>
                <tbody>
                  {(auditLogs.data || []).slice(0, 50).map((log: any) => (
                    <tr key={log.id}>
                      <td className="mono">{new Date(log.createdAt).toLocaleString()}</td>
                      <td>{log.action}</td>
                      <td>{log.actorUser?.email || 'System'}</td>
                      <td>
                        {log.targetType} <span className="mono">{log.targetId?.slice(0, 8) || ''}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
